// frontend/modules/customer-notification/components/dashboard/NotificationActivity.tsx
import React from 'react';
import { Card, Table, Tag, Typography, Badge, Space, Tooltip, Empty, Spin } from 'antd';
import { 
    CheckCircleOutlined, 
    CloseCircleOutlined,
    WhatsAppOutlined,
    MailOutlined,
    MessageOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { CustomerNotificationHistory, NotificationPlatforms } from '@/shared/api/model';
import { Link } from 'react-router-dom';

interface NotificationActivityProps {
    notifications: CustomerNotificationHistory[];
    isLoading: boolean;
}

const { Text } = Typography;

const NotificationActivity: React.FC<NotificationActivityProps> = ({ notifications, isLoading }) => {
    const getStatusIcon = (success: boolean) => {
        return success 
            ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> 
            : <CloseCircleOutlined style={{ color: '#f5222d' }} />;
    };
    
    const getPlatformTag = (platform: NotificationPlatforms) => {
        switch (platform) {
            case NotificationPlatforms.SMS:
                return (
                    <Tag icon={<MessageOutlined />} color="blue">
                        SMS
                    </Tag>
                );
                
            case NotificationPlatforms.WHATS_APP:
                return (
                    <Tag icon={<WhatsAppOutlined />} color="green">
                        WhatsApp
                    </Tag>
                );
                
            case NotificationPlatforms.EMAIL:
                return (
                    <Tag icon={<MailOutlined />} color="purple">
                        Email
                    </Tag>
                );
                
            default:
                return (
                    <Tag>
                        {platform}
                    </Tag>
                );
        }
    };
    
    const columns = [
        {
            title: 'Timestamp',
            dataIndex: 'sentAt',
            key: 'sentAt',
            render: (sentAt: string) => (
                <Text className="text-xs">
                    {new Date(sentAt).toLocaleString()}
                </Text>
            )
        },
        {
            title: 'Status',
            dataIndex: 'success',
            key: 'success',
            render: (success: boolean) => (
                <Badge 
                    status={success ? "success" : "error"} 
                    text={success ? "Delivered" : "Failed"} 
                />
            )
        },
        {
            title: 'Template',
            dataIndex: 'templateName',
            key: 'templateName',
            render: (templateName: string) => (
                <Text ellipsis>
                    {templateName}
                </Text>
            )
        },
        {
            title: 'Platform',
            dataIndex: 'platform',
            key: 'platform',
            render: (platform: NotificationPlatforms) => getPlatformTag(platform)
        },
        {
            title: 'Recipient',
            dataIndex: 'recipient',
            key: 'recipient',
            render: (recipient: string, record: CustomerNotificationHistory) => (
                <Tooltip title={`Customer: ${record.branch}/${record.customerNumber}`}>
                    <Text ellipsis>
                        {recipient}
                    </Text>
                </Tooltip>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (action:any, record: CustomerNotificationHistory) => (
                <Space size="small">
                    <Tooltip title="View Details">
                        <Link to={`/notifications/history?id=${record.id}`}>
                            <EyeOutlined />
                        </Link>
                    </Tooltip>
                </Space>
            )
        }
    ];
    
    return (
        <Card 
            title="Recent Notification Activity" 
            extra={
                <Link to="/notifications/history">
                    View All
                </Link>
            }
            className="mb-4"
        >
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Spin size="large" />
                </div>
            ) : notifications.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recent notifications" />
            ) : (
                <Table 
                    dataSource={notifications} 
                    columns={columns} 
                    rowKey="id" 
                    size="small"
                    pagination={{ pageSize: 5 }}
                />
            )}
        </Card>
    );
};

export default NotificationActivity;
